import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { auth } from '@/lib/supabase'
import { influencerService } from '@/lib/influencerService'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null)
  const session = ref(null)
  const loading = ref(false)
  const error = ref(null)

  // Getters
  const isAuthenticated = computed(() => !!user.value)
  const userEmail = computed(() => user.value?.email)
  const userId = computed(() => user.value?.id)

  // Actions
  const initializeAuth = async () => {
    loading.value = true
    try {
      // Get current session
      const { session: currentSession } = await auth.getSession()
      if (currentSession) {
        session.value = currentSession
        user.value = currentSession.user
      }

      // Listen for auth changes
      auth.onAuthStateChange((event, currentSession) => {
        session.value = currentSession
        user.value = currentSession?.user || null
        
        if (event === 'SIGNED_OUT') {
          // Clear any additional state here
        }
      })
    } catch (err) {
      error.value = err.message
    } finally {
      loading.value = false
    }
  }

  const signUp = async (email, password, userData = {}) => {
    loading.value = true
    error.value = null
    
    try {
      const { data, error: signUpError } = await auth.signUp(email, password, userData)
      
      if (signUpError) {
        error.value = signUpError.message
        return { success: false, error: signUpError.message }
      }
      
      return { success: true, data }
    } catch (err) {
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  const signIn = async (email, password) => {
    loading.value = true
    error.value = null
    
    try {
      // First check if the influencer is approved
      const influencer = await influencerService.getInfluencer(email)
      
      if (influencer.status !== 'approved') {
        error.value = 'Your influencer application is still under review. Please wait for approval before signing in.'
        return { success: false, error: 'Your influencer application is still under review. Please wait for approval before signing in.' }
      }
      
      const { data, error: signInError } = await auth.signIn(email, password)
      
      if (signInError) {
        error.value = signInError.message
        return { success: false, error: signInError.message }
      }
      
      user.value = data.user
      session.value = data.session
      
      return { success: true, data }
    } catch (err) {
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  const sendMagicLink = async (email) => {
    loading.value = true
    error.value = null
    
    try {
      // First check if the influencer is approved
      const influencer = await influencerService.getInfluencer(email)
      
      if (influencer.status !== 'approved') {
        error.value = 'Your influencer application is still under review. Please wait for approval before signing in.'
        return { success: false, error: 'Your influencer application is still under review. Please wait for approval before signing in.' }
      }
      
      // Send magic link via Supabase
      const { error: magicLinkError } = await auth.sendMagicLink(email, {
        redirectTo: `${window.location.origin}/dashboard`
      })
      
      if (magicLinkError) {
        error.value = magicLinkError.message
        return { success: false, error: magicLinkError.message }
      }
      
      return { success: true }
    } catch (err) {
      // Check if it's a 404 error (user hasn't applied)
      if (err.message.includes('404') || err.message.includes('Not Found')) {
        error.value = 'You have not yet applied to be an influencer.'
        return { success: false, error: 'You have not yet applied to be an influencer.' }
      }
      
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  const signInWithOAuth = async (provider) => {
    loading.value = true
    error.value = null
    
    try {
      const { data, error: oauthError } = await auth.signInWithOAuth(provider)
      
      if (oauthError) {
        error.value = oauthError.message
        return { success: false, error: oauthError.message }
      }
      
      // Since we're doing pre-check in the component, we can trust the user is approved
      user.value = data.user
      session.value = data.session
      
      return { success: true, data }
    } catch (err) {
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  const signOut = async () => {
    loading.value = true
    error.value = null
    
    try {
      const { error: signOutError } = await auth.signOut()
      
      if (signOutError) {
        error.value = signOutError.message
        return { success: false, error: signOutError.message }
      }
      
      user.value = null
      session.value = null
      
      return { success: true }
    } catch (err) {
      error.value = err.message
      return { success: false, error: err.message }
    } finally {
      loading.value = false
    }
  }

  const clearError = () => {
    error.value = null
  }

  return {
    // State
    user,
    session,
    loading,
    error,
    
    // Getters
    isAuthenticated,
    userEmail,
    userId,
    
    // Actions
    initializeAuth,
    signUp,
    signIn,
    sendMagicLink,
    signInWithOAuth,
    signOut,
    clearError
  }
}) 